import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall } from "../utils/apiResponseHandler";
import type { Notification as AppNotification } from "../types/notifications";

// Update the response type to match the actual API response
interface NotificationsMeta {
  page: number;
  nextPage: number | null;
  prevPage: number | null;
  offset: number;
  totalItems: number;
  totalPages: number;
  itemCount: number;
}

interface NotificationsData {
  meta: NotificationsMeta;
  items: AppNotification[];
}

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

export const notificationService = {
  getNotifications: async (page = 1, limit = 10, agencyId?: string): Promise<NotificationsData> => {
    const result = await handleApiCall<NotificationsData | ApiResponse<NotificationsData>>(async () => {
      // Scope notifications to the active agency so users with memberships in
      // multiple agencies don't see other agencies' notifications (e.g.
      // leaderboard alerts). The x-agency-id header also carries this, but
      // passing it explicitly keeps the request self-describing.
      const agencyParam = agencyId ? `&agencyId=${encodeURIComponent(agencyId)}` : '';
      return await api.get<ApiResponse<NotificationsData>>({
        url: getApiUrl(`${endpoint.notifications.getNotifications}?page=${page}&limit=${limit}${agencyParam}`)
      });
    });
    const payload = (result as any)?.data ?? result;
    return payload as NotificationsData;
  },

  getNotificationById: async (id: string): Promise<AppNotification> => {
    const result = await handleApiCall<AppNotification | ApiResponse<AppNotification>>(async () => {
      return await api.get<ApiResponse<AppNotification>>({
        url: getApiUrl(endpoint.notifications.getNotificationById(id))
      });
    });
    const payload = (result as any)?.data ?? result;
    return payload as AppNotification;
  },

  markAsRead: async (id: string): Promise<{ message: string }> => {
    const result = await handleApiCall<{ message: string } | ApiResponse<{ message: string }>>(async () => {
      return await api.put({
        url: getApiUrl(endpoint.notifications.markAsRead(id)),
        body: {}
      });
    });
    const payload = (result as any)?.data ?? result;
    return payload as { message: string };
  },

  dismissNotification: async (id: string): Promise<{ message: string }> => {
    const result = await handleApiCall<{ message: string } | ApiResponse<{ message: string }>>(async () => {
      return await api.delete({
        url: getApiUrl(endpoint.notifications.dismissNotification(id)),
      });
    });
    const payload = (result as any)?.data ?? result;
    return payload as { message: string };
  },

  dismissAllNotifications: async (): Promise<{ message: string; count: number }> => {
    const result = await handleApiCall<{ message: string; count: number } | ApiResponse<{ message: string; count: number }>>(async () => {
      return await api.delete({
        url: getApiUrl(endpoint.notifications.dismissAllNotifications),
      });
    });
    const payload = (result as any)?.data ?? result;
    return payload as { message: string; count: number };
  },
};